import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { ChevronRight, Save, Check, X } from 'lucide-react';


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ShoppingPreferencesScreen = () => {
  const { setCurrentScreen } = useApp();
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [preferences, setPreferences] = useState({
    preferred_fabrics: [],
    sustainability_priority: 'medium',
    price_sensitivity: 'mid',
    preferred_categories: [],
    avoid_materials: {} // { Material: tolerancePercent }
  });

  const fabricOptions = ['Cotton', 'Silk', 'Wool', 'Linen', 'Cashmere', 'Lyocell', 'Modal', 'Bamboo'];
  const categoryOptions = ['Shirts', 'Dresses', 'Pants', 'Jeans', 'Jackets', 'Knitwear', 'Activewear'];
  const avoidOptions = ['Polyester', 'Nylon', 'Acrylic', 'Leather', 'Fur', 'Wool', 'Silk'];

  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) return;
        const res = await fetch(`${BACKEND_URL}/api/auth/preferences`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const result = await res.json();
          if (result.data) {
            setPreferences({
              preferred_fabrics: result.data.preferred_fabrics || [],
              sustainability_priority: result.data.sustainability_priority || 'medium',
              price_sensitivity: result.data.price_sensitivity || 'mid',
              preferred_categories: result.data.preferred_categories || [],
              avoid_materials: (() => {
                const raw = result.data.avoid_materials;
                if (!raw) return {};
                // Migrate old array format → object with 0% tolerance
                if (Array.isArray(raw)) {
                  return Object.fromEntries(raw.map(m => [m, 0]));
                }
                return raw;
              })(),
            });
          }
        }
      } catch (e) {
        console.error('Error loading preferences:', e);
      } finally {
        setLoaded(true);
      }
    };
    loadPreferences();
  }, []);

  const toggleItem = (array, item) => {
    if (array.includes(item)) {
      return array.filter(i => i !== item);
    } else {
      return [...array, item];
    }
  };

  const toggleMaterial = (material) => {
    const current = preferences.avoid_materials;
    if (material in current) {
      const updated = { ...current };
      delete updated[material];
      return updated;
    }
    return { ...current, [material]: 0 };
  };

  const setMaterialTolerance = (material, value) => {
    const pct = Math.min(100, Math.max(0, parseInt(value) || 0));
    setPreferences({ ...preferences, avoid_materials: { ...preferences.avoid_materials, [material]: pct } });
  };

  const toleranceLabel = (pct) => pct === 0 ? 'Strict' : `≤${pct}%`;

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_URL}/api/auth/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(preferences)
      });

      if (response.ok) {
        setCurrentScreen('profile');
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pb-24">
      {/* Header */}
      <div className="px-6 pt-6 pb-4 flex items-center gap-4">
        <button
          onClick={() => setCurrentScreen('profile')}
          className="w-10 h-10 bg-black/5 rounded-xl flex items-center justify-center hover:bg-black/10 transition-all flex-shrink-0"
        >
          <ChevronRight size={18} className="text-black rotate-180" />
        </button>
        <img src="/logos/logo-black.jpg" alt="ATELIA" className="h-24 w-auto object-contain" />
      </div>

      <div className="px-6 pb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-black/55 mb-2 font-bold">PERSONALIZATION</p>
        <h1 className="text-2xl font-bold text-black uppercase tracking-wide">Shopping Preferences</h1>
      </div>

      <div className="px-6 space-y-6">
        {/* Preferred Fabrics */}
        <div>
          <label className="text-xs uppercase tracking-wide text-black/55 mb-3 block font-bold">Preferred Fabrics</label>
          <div className="flex flex-wrap gap-2">
            {fabricOptions.map((fabric) => {
              const isSelected = preferences.preferred_fabrics.includes(fabric);
              return (
                <button
                  key={fabric}
                  onClick={() => setPreferences({
                    ...preferences,
                    preferred_fabrics: toggleItem(preferences.preferred_fabrics, fabric)
                  })}
                  className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all ${
                    isSelected
                      ? 'bg-black text-white'
                      : 'bg-white border border-black/12 text-black hover:bg-black/5'
                  }`}
                >
                  {isSelected && <Check size={12} className="inline mr-1" />}
                  {fabric}
                </button>
              );
            })}
          </div>
        </div>

        {/* Sustainability Priority */}
        <div>
          <label className="text-xs uppercase tracking-wide text-black/55 mb-3 block font-bold">Sustainability Priority</label>
          <div className="flex gap-2">
            {['Low', 'Medium', 'High'].map((level) => (
              <button
                key={level}
                onClick={() => setPreferences({...preferences, sustainability_priority: level.toLowerCase()})}
                className={`flex-1 py-3 rounded-xl text-sm font-bold uppercase transition-all ${
                  preferences.sustainability_priority === level.toLowerCase()
                    ? 'bg-black text-white'
                    : 'bg-white border border-black/12 text-black hover:bg-black/5'
                }`}
              >
                {level}
              </button>
            ))}
          </div>
        </div>

        {/* Price Sensitivity */}
        <div>
          <label className="text-xs uppercase tracking-wide text-black/55 mb-3 block font-bold">Price Sensitivity</label>
          <div className="grid grid-cols-2 gap-2">
            {['Budget', 'Mid', 'Premium', 'Luxury'].map((tier) => (
              <button
                key={tier}
                onClick={() => setPreferences({...preferences, price_sensitivity: tier.toLowerCase()})}
                className={`py-3 rounded-xl text-sm font-bold uppercase transition-all ${
                  preferences.price_sensitivity === tier.toLowerCase()
                    ? 'bg-black text-white'
                    : 'bg-white border border-black/12 text-black hover:bg-black/5'
                }`}
              >
                {tier}
              </button>
            ))}
          </div>
        </div>

        {/* Preferred Categories */}
        <div>
          <label className="text-xs uppercase tracking-wide text-black/55 mb-3 block font-bold">Preferred Categories</label>
          <div className="flex flex-wrap gap-2">
            {categoryOptions.map((category) => {
              const isSelected = preferences.preferred_categories.includes(category);
              return (
                <button
                  key={category}
                  onClick={() => setPreferences({
                    ...preferences,
                    preferred_categories: toggleItem(preferences.preferred_categories, category)
                  })}
                  className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all ${
                    isSelected
                      ? 'bg-black text-white'
                      : 'bg-white border border-black/12 text-black hover:bg-black/5'
                  }`}
                >
                  {isSelected && <Check size={12} className="inline mr-1" />}
                  {category}
                </button>
              );
            })}
          </div>
        </div>

        {/* Avoid Materials */}
        <div>
          <label className="text-xs uppercase tracking-wide text-black/55 mb-1 block font-bold">Materials to Avoid</label>
          <p className="text-xs text-black/40 mb-3">Tap to select, then set your max tolerable %</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {avoidOptions.map((material) => {
              const isSelected = material in preferences.avoid_materials;
              const tolerance = preferences.avoid_materials[material];
              return (
                <button
                  key={material}
                  onClick={() => setPreferences({ ...preferences, avoid_materials: toggleMaterial(material) })}
                  className={`px-3 py-2 rounded-xl text-xs font-bold uppercase transition-all flex items-center gap-1.5 ${
                    isSelected ? 'bg-black text-white' : 'bg-white border border-black/12 text-black hover:bg-black/5'
                  }`}
                >
                  {material}
                  {isSelected && (
                    <span className="text-[10px] font-normal bg-white/20 px-1.5 py-0.5 rounded-md">
                      {tolerance === 0 ? 'Strict' : `≤${tolerance}%`}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Tolerance steppers for selected materials */}
          {Object.keys(preferences.avoid_materials).length > 0 && (
            <div className="bg-black/5 rounded-xl p-4 space-y-3">
              <p className="text-[10px] uppercase font-bold text-black/40 tracking-wide">Max Tolerable %</p>
              {Object.entries(preferences.avoid_materials).map(([material, tolerance]) => (
                <div key={material} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPreferences({ ...preferences, avoid_materials: toggleMaterial(material) })}
                      className="w-5 h-5 rounded-full bg-black/10 flex items-center justify-center hover:bg-black/20 transition-colors"
                    >
                      <X size={10} className="text-black/50" />
                    </button>
                    <span className="text-sm font-bold uppercase">{material}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setMaterialTolerance(material, Math.max(0, tolerance - 5))}
                      className="w-8 h-8 rounded-lg bg-white border border-black/12 flex items-center justify-center text-lg font-bold text-black hover:bg-black/5 transition-colors"
                    >−</button>
                    <div className="flex items-center gap-0.5 w-16 justify-center">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={tolerance}
                        onChange={(e) => setMaterialTolerance(material, e.target.value)}
                        className="w-10 text-center text-sm font-bold bg-transparent outline-none border-b-2 border-black/20 focus:border-black [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      />
                      <span className="text-sm text-black/50">%</span>
                    </div>
                    <button
                      onClick={() => setMaterialTolerance(material, Math.min(100, tolerance + 5))}
                      className="w-8 h-8 rounded-lg bg-white border border-black/12 flex items-center justify-center text-lg font-bold text-black hover:bg-black/5 transition-colors"
                    >+</button>
                  </div>
                </div>
              ))}
              <p className="text-[10px] text-black/35 pt-1">0% = avoid completely · higher % = acceptable in blends</p>
            </div>
          )}
        </div>
      </div>

      {/* Save Button */}
      <div className="px-6 mt-8">
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-black text-white py-4 rounded-xl font-bold uppercase
            flex items-center justify-center gap-2 hover:bg-black/90 transition-all
            disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save size={18} />
          {saving ? 'Saving...' : 'Save Preferences'}
        </button>
      </div>
    </div>
  );
};

export default ShoppingPreferencesScreen;